import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, CheckCircle2, MessageCircle } from "lucide-react";
import { SERVICES_MENU, waLink } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = [...SERVICES_MENU.map((s) => s.name), "Reiki Healing (Studio — Launching Soon)", "Complete Reading (Numerology + Graphology)", "Other / Not Sure"];

export const BookingModal = ({ open, onClose }) => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", service: SERVICES[1], mode: "online", message: "" });
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/bookings`, { ...form, email: form.email || null, message: form.message || null });
            setDone(true);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const close = () => {
        setDone(false);
        onClose();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="booking-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                    onClick={close}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 40, scale: 0.96 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0E1B33] p-8 max-h-[90vh] overflow-y-auto shadow-[0_0_80px_rgba(201,169,98,0.12)]"
                    >
                        <button data-testid="booking-close" onClick={close} aria-label="Close"
                            className="absolute top-5 right-5 text-[#5C5A56] hover:text-[#C9A962] transition-colors">
                            <X size={20} />
                        </button>

                        {!done ? (
                            <>
                                <h3 className="font-display text-2xl font-semibold text-[#F0EBE0]">Book a Consultation</h3>
                                <p className="mt-2 text-sm font-light text-[#A19D94]">Share your details — Maitrii will confirm your session personally.</p>
                                <form data-testid="booking-form" onSubmit={submit} className="mt-7 space-y-4">
                                    <input data-testid="booking-name" required type="text" placeholder="Your Name" value={form.name} onChange={set("name")}
                                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-[#F0EBE0] placeholder-[#5C5A56]" />
                                    <input data-testid="booking-phone" required type="tel" placeholder="Phone / WhatsApp Number" value={form.phone} onChange={set("phone")}
                                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-[#F0EBE0] placeholder-[#5C5A56]" />
                                    <input data-testid="booking-email" type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")}
                                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-[#F0EBE0] placeholder-[#5C5A56]" />
                                    <select data-testid="booking-service" value={form.service} onChange={set("service")}
                                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-[#F0EBE0]">
                                        {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <div className="grid grid-cols-2 gap-3">
                                        {["online", "in-person"].map((m) => (
                                            <button key={m} type="button" data-testid={`booking-mode-${m}`} onClick={() => setForm({ ...form, mode: m })}
                                                className={`py-3 rounded-xl border text-xs uppercase tracking-[0.15em] transition-all ${
                                                    form.mode === m ? "border-[#C9A962] bg-[#C9A962]/10 text-[#C9A962]" : "border-white/10 text-[#A19D94] hover:border-white/25"
                                                }`}>
                                                {m === "online" ? "Online" : "In-Person"}
                                            </button>
                                        ))}
                                    </div>
                                    <textarea data-testid="booking-message" rows={3} placeholder="Anything you'd like to share? (optional)" value={form.message} onChange={set("message")}
                                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-[#F0EBE0] placeholder-[#5C5A56] resize-none" />
                                    <button data-testid="booking-submit" type="submit" disabled={loading}
                                        className="w-full py-4 rounded-full bg-[#C9A962] text-[#081226] text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#dcc083] hover:shadow-[0_0_30px_rgba(201,169,98,0.45)] transition-all disabled:opacity-50">
                                        {loading ? "Sending…" : "Request Booking"}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <motion.div data-testid="booking-success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                                <CheckCircle2 size={56} className="text-[#C9A962] mx-auto mb-5" />
                                <h3 className="font-display text-2xl font-semibold text-[#F0EBE0]">Request Received</h3>
                                <p className="mt-3 text-sm font-light text-[#A19D94] max-w-sm mx-auto leading-relaxed">
                                    Thank you, {form.name}. Your {form.service} request has been noted. For a faster confirmation, message us directly on WhatsApp.
                                </p>
                                <a data-testid="booking-whatsapp-confirm"
                                    href={waLink(`Hi Maitrii! I'm ${form.name}. I just requested a ${form.service} (${form.mode}) consultation on the website.`)}
                                    target="_blank" rel="noopener noreferrer"
                                    className="mt-7 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#C9A962] text-[#081226] text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#dcc083] transition-all">
                                    <MessageCircle size={16} /> Confirm on WhatsApp
                                </a>
                            </motion.div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
